import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartLine,
  faEye,
  faShieldHalved
} from '@fortawesome/free-solid-svg-icons';
import { Card, Col } from 'react-bootstrap';
import DomainAuthorityHistory from '@/app/Serp/components/results/DomainAuthorityHistory';
import numberHumanizer from '@/helpers/numberHumanizer';
import { getColor } from '@/helpers/utils';
import type { GapCompetitorDomainMetricsData } from '@/app/KeywordResearch/types/gapApiTypes';
import type { SerpAnalysisDomains } from '@/app/Serp/types/serpApiTypes';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import HelpTooltip from '@/app/common/components/HelpTooltip';

type CompetitorDomainMetricsPanelProps = {
  data: GapCompetitorDomainMetricsData;
};

type MetricItem = {
  key: string;
  label: string;
  value: string;
  icon: IconDefinition;
  accent: string;
  tooltip: string;
};

const getDomainAuthorityColor = (num: number) => {
  if (num >= 0 && num < 15) return getColor('success');
  if (num >= 15 && num < 30) return getColor('info');
  if (num >= 30 && num < 50) return '#bb881d';
  if (num >= 50 && num < 70) return getColor('warning');
  return getColor('danger');
};

const getSpamColor = (num: number) => {
  if (num >= 0 && num < 30) return getColor('success');
  if (num >= 30) return getColor('warning');
  return getColor('danger');
};

const MetricCard = ({
  label,
  value,
  icon,
  accent,
  tooltip
}: Omit<MetricItem, 'key'>) => (
  <Card
    className="h-100 mb-0 border"
    style={{
      borderColor: `${accent}55`,
      boxShadow: `inset 3px 0 0 ${accent}`
    }}
  >
    <Card.Body className="d-flex align-items-center justify-content-between gap-3 py-3 px-3">
      <div
        className="rounded-3 d-inline-flex align-items-center justify-content-center flex-shrink-0 text-white"
        style={{
          width: '2.75rem',
          height: '2.75rem',
          backgroundColor: accent,
          boxShadow: `0 8px 16px ${accent}33`
        }}
      >
        <FontAwesomeIcon icon={icon} />
      </div>
      <div className="min-w-0 text-start flex-grow-1">
        <div className="fs--1 text-600 text-truncate">
          {label} <HelpTooltip html={tooltip} />
        </div>
        <div
          className="fs-2 fw-bold lh-1 mt-3 text-truncate"
          style={{ color: accent, letterSpacing: '-0.02em' }}
          title={value}
        >
          {value}
        </div>
      </div>
    </Card.Body>
  </Card>
);

const CompetitorDomainMetricsPanel = ({
  data
}: CompetitorDomainMetricsPanelProps) => {
  const visits =
    data.daily_visits != null
      ? numberHumanizer(data.daily_visits).toPersianDigit()
      : '—';
  const authority =
    data.moz_domain_authority != null
      ? String(data.moz_domain_authority).toPersianDigit()
      : '—';
  const spam =
    data.moz_spam_score != null
      ? String(data.moz_spam_score).toPersianDigit()
      : '—';

  const authorityAccent =
    data.moz_domain_authority != null
      ? getDomainAuthorityColor(data.moz_domain_authority)
      : getColor('info');
  const spamAccent =
    data.moz_spam_score != null
      ? getSpamColor(data.moz_spam_score)
      : getColor('success');
  const visitsAccent = getColor('primary');

  const metrics: MetricItem[] = [
    {
      key: 'visits',
      label: 'تخمین میانگین بازدید ماهانه',
      value: visits,
      icon: faEye,
      accent: visitsAccent,
      tooltip:
        'تعداد تقریبی بازدیدهایی که این دامنه در طول یک ماه دریافت می‌کند.'
    },
    {
      key: 'authority',
      label: 'دامین آتوریتی',
      value: authority,
      icon: faChartLine,
      accent: authorityAccent,
      tooltip:
        'امتیاز اعتبار و قدرت دامنه از ۰ تا ۱۰۰ است؛ امتیاز بالاتر نشان‌دهنده اعتبار بیشتر دامنه است.'
    },
    {
      key: 'spam',
      label: 'اسپم اسکور',
      value: spam,
      icon: faShieldHalved,
      accent: spamAccent,
      tooltip:
        'درصد احتمال وجود سیگنال‌های اسپم در دامنه است؛ امتیاز پایین‌تر وضعیت بهتری را نشان می‌دهد.'
    }
  ];

  return (
    <>
      {metrics.map(({ key, ...metric }) => (
        <Col key={key} xs={12} md={4}>
          <MetricCard {...metric} />
        </Col>
      ))}

      <Col xs={12}>
        <DomainAuthorityHistory
          legendBottom
          data={data.domains_moz as SerpAnalysisDomains['domains_moz']}
        />
      </Col>
    </>
  );
};

export default CompetitorDomainMetricsPanel;
